import { DataCollectionAppName } from '../data-collection-app/data-collection-app.models';
import { EventType } from '../game/game.models';
import { BySport } from '../sport/sport.models';
import { StateSliceDefaults, StateSliceName } from '../store.models';
import { UserForSportAPI } from '../user/user.models';

export interface ScoringPolicyAPI {
    name: string;
    description: string | null;
    id: string;
}

export interface AssessmentTypeAPI {
    app: DataCollectionAppName;
    name: string;
    description: string | null;
    byTeam?: boolean;
    id: string;
}

export enum AssignmentStateNameAPI {
    ASSIGNED = 'Assigned',
    IN_PROGRESS = 'In Progress',
    COMPLETED = 'Completed'
}

export interface AssignmentStateAPI {
    name: AssignmentStateNameAPI;
    id: string;
}

export interface PostAssessmentParams {
    name: string;
    description: string | null;
    originalGameId: string;
    active: boolean;
    segments: AssessmentSegmentAPI[];
    scoringPolicyDefId: string;
    expectedDurationInSeconds: number | null;
    assessmentTypeDefId: string;
}

export interface AssessmentSegmentAPI {
    period: number;
    teamId: string | null;
    frameStart: number;
    frameEnd: number;
}

export interface AssessmentAPI {
    id: string;
    createdOn: string;
    trainingGameId: string;
    name: string;
    description: string | null;
    originalGameId: string;
    active: boolean;
    segments: AssessmentSegmentAPI[];
    scoringPolicyDefId: string;
    expectedDurationInSeconds: number | null;
    assessmentTypeDefId: string;
    maxScore: number;
}

export interface GetAssessmentsParamsAPI {
    withscoringpolicydefinitions?: boolean;
    withassessmenttypedefinitions?: boolean;
    includeinactive?: boolean;
}

export interface GetAssessmentsResponseAPI {
    assessments: AssessmentAPI[];
    scoringPolicyDefinitions?: ScoringPolicyAPI[] | null;
    assessmentTypeDefinitions?: AssessmentTypeAPI[] | null;
}

export interface PatchAssessmentParamsAPI {
    active: boolean;
    lastUpdatedOn: string;
}

export interface AssignmentAPI {
    id: string;
    gameId: string | null;
    assignmentStateDefId: string;
    /** Score as a percentage */
    score: number | null;
    assessmentId: string;
    userId: string;
    startedOn: string | null;
    updatedOn: string;
    completedOn: string | null;
    createdOn: string;
}

export interface GetAssignmentsParamsAPI {
    userid?: string[];
    companyid?: string[];
    assigneddatefrom?: string;
    assigneddateto?: string;
    scoringpolicydefid?: string[];
    assignmentstatedefid?: string[];
    assessmentid?: string[];
    assessmenttypedefid?: string[];
    withusers?: boolean;
    withassignmentstates?: boolean;
    withassessments?: boolean;
}

export interface PostAssignmentsParamsAPI {
    userId: string;
    assessmentIds: string[];
}

export interface PostAssignmentsResponseAPI {
    userId: string;
    assignmentIds: string[];
}

export interface GetAssignmentsResponseAPI {
    assignments: AssignmentAPI[];
    /** Assessment definitions by assessment definition ID */
    assessments?: Record<string, AssessmentAPI>;
    /** Assignment states by assignment state ID */
    assignmentStates?: Record<string, AssignmentStateAPI>;
    /** Users by user ID */
    users?: Record<string, UserForSportAPI>;
}

export interface PatchAssignmentParamsAPI {
    assignmentStateId: string;
    updatedOn: string;
}

export enum GradingType {
    INCORRECT = 'incorrect',
    MISSING = 'missing',
    UNWARRANTED = 'unwarranted'
}

export interface AssignmentGradingAPI {
    assignmentId: string;
    correctCount: number;
    incorrect: AssignmentGradingEventReportAPI[];
    missing: AssignmentGradingEventReportAPI[];
    unwarranted: AssignmentGradingEventReportAPI[];
}

export interface AssignmentGradingEventReportAPI {
    details: AssignmentGradingDetailAPI[];
    /** type of the event, e.g. 'player', 'playershift' */
    appEventType: EventType;
    eventFrame: number;
    eventId: string;
    eventPeriod: number;
    eventTeamId: string;
    vidId: string;
    videoTimeStamp: string;
}

export interface AssignmentGradingDetailAPI {
    key: string;
    /** The computed value of the grading (e.g. what displays in the table) */
    computedValue: string | number | boolean;
    /** The raw master value for the event */
    masterValue: string | number | boolean;
    /** The raw value from the assignment */
    assignmentValue: string | number | boolean;
    isSuccessful: boolean;
}

export interface GetAssignmentGradingsParamsAPI {
    userid?: string[];
    withassessments?: boolean;
    withassignments?: boolean;
    assignmentids?: string[];
    withusers?: boolean;
}

export interface GetAssignmentGradingsResponseAPI {
    gradings: AssignmentGradingAPI[];
    assignments: {
        [assignmentId: string]: AssignmentAPI;
    } | null;
    assessments: {
        [assignmentId: string]: AssessmentAPI;
    } | null;
    users: {
        [assignmentId: string]: UserForSportAPI;
    } | null;
}

export interface AssessmentStateModel {
    scoringPoliciesBySport: BySport<ScoringPolicyAPI[]> | undefined;
    assessmentTypesBySport: BySport<AssessmentTypeAPI[]> | undefined;
    assignmentStatesBySport: BySport<AssignmentStateAPI[]> | undefined;
}

export const assessmentStateDefaults: StateSliceDefaults<AssessmentStateModel> = Object.freeze({
    sliceName: StateSliceName.ASSESSMENT,
    defaults: {
        scoringPoliciesBySport: undefined,
        assessmentTypesBySport: undefined,
        assignmentStatesBySport: undefined
    }
});
